import { ExternalLinkIcon } from "@radix-ui/react-icons";
import type { TaskDue } from "client/openapi/models/TaskDue";
import moment from "moment";
import { useState } from "react";
import { GoChecklist } from "react-icons/go";
import DismissButton from "./DismissButton";
import IconTip from "./IconTip";
import TaskViewDialog from "./TaskViewDialog";

/**
 * Tasks due about a person: one chip for those overdue ("Tasks overdue
 * (2)") and one for those due ("Task due"), each opening its tasks in a
 * window over the dashboard.
 */
export function TaskChips({
  tasks,
  orgId,
  onChange,
}: {
  tasks: TaskDue[];
  orgId: number;
  /** A task was edited or marked done. */
  onChange: () => void;
}) {
  const [open, setOpen] = useState<TaskDue[]>();
  const groups = [
    {
      tasks: tasks.filter((task) => task.overdue),
      one: "Task overdue",
      many: "Tasks overdue",
      className: "people-chip--overdue",
    },
    {
      tasks: tasks.filter((task) => !task.overdue),
      one: "Task due",
      many: "Tasks due",
      className: "people-chip--task",
    },
  ].filter((group) => group.tasks.length > 0);

  return (
    <>
      {groups.map((group) => (
        <button
          key={group.one}
          type="button"
          className={`people-chip ${group.className}`}
          title={group.tasks.map((task) => task.name).join(", ")}
          onClick={() => setOpen(group.tasks)}
        >
          {group.tasks.length === 1
            ? group.one
            : `${group.many} (${group.tasks.length})`}
        </button>
      ))}
      {open && (
        <TaskViewDialog
          tasks={open}
          orgId={orgId}
          onClose={() => setOpen(undefined)}
          onChanged={onChange}
        />
      )}
    </>
  );
}

/** A task due that names no one: its own Needs attention row. */
export function TaskTableRow({
  task,
  orgId,
  onDismiss,
  onChange,
}: {
  task: TaskDue;
  orgId: number;
  /** Hides the row from Needs attention; the task itself is untouched. */
  onDismiss: () => void;
  /** The task was edited or marked done. */
  onChange: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <tr>
      <td>
        <div className="people-panel__person">
          <span className="people-panel__task-icon" aria-hidden>
            <GoChecklist />
          </span>
          <div className="min-w-0">
            <button
              type="button"
              className="people-panel__name"
              onClick={() => setOpen(true)}
            >
              {task.name}
            </button>
            <p className="mt-0.5 small-text text-neutral-500">
              Due {moment(task.due_date).format("ddd, MMM D")}
            </p>
          </div>
        </div>
      </td>
      <td>
        <span className="people-chip people-chip--kind-task">Task</span>
      </td>
      <td className="hidden lg:table-cell">–</td>
      <td className="hidden md:table-cell">–</td>
      <td className="people-panel__status">
        <div className="flex flex-wrap gap-1">
          <TaskChips tasks={[task]} orgId={orgId} onChange={onChange} />
        </div>
      </td>
      <td className="people-panel__actions">
        {/* Open task and Dismiss in the people rows' Open page and Dismiss
            columns. */}
        <div className="flex items-center justify-end gap-1">
          <span className="people-panel__icon-slot" aria-hidden />
          <span className="people-panel__icon-slot" aria-hidden />
          <IconTip label="Open task">
            <button
              type="button"
              className="people-panel__icon"
              aria-label="Open task"
              onClick={() => setOpen(true)}
            >
              <ExternalLinkIcon />
            </button>
          </IconTip>
          <DismissButton onDismiss={onDismiss} />
        </div>
        {open && (
          <TaskViewDialog
            tasks={[task]}
            orgId={orgId}
            onClose={() => setOpen(false)}
            onChanged={onChange}
          />
        )}
      </td>
    </tr>
  );
}
